import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import { 
  Brain, Sparkles, TrendingUp, Users, Baby, PoundSterling, 
  BarChart3, PieChart as PieIcon, Send, RefreshCw, Crown,
  Trophy, Target, Calendar, FileText, Bot, Loader2,
  Download, Printer, Mail, Upload, Trash2, Eye, FileUp,
  CheckCircle, AlertCircle, Globe, Search, MessageSquare, FileSearch
} from 'lucide-react';
import { toast } from 'sonner';

// ICRCL Official Contact Details
const ICRCL_NAME = "Innovative Care and Rehab Centre Limited (ICRCL)";
const ICRCL_ADDRESS = "City Gate House, 246-250 Romford Road, London, E7 9HZ";
const ICRCL_PHONE = "+44 7380 350059";
const ICRCL_EMAIL = "info@icrcl.co.uk";

const COLORS = ['#2a9d8f', '#1e3a5f', '#e76f51', '#f4a261', '#264653', '#e9c46a', '#8ecae6', '#219ebc'];

export default function AnalyticsDashboardPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('month');
  const printRef = useRef(null);
  
  // Permissions state
  const [permissions, setPermissions] = useState({
    can_view: false,
    can_generate_ai: false,
    can_export: false,
    can_print: false,
    can_email: false,
    can_upload_interventions: false,
    is_super_admin: false
  });
  
  // Data states
  const [dashboardData, setDashboardData] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const [staffData, setStaffData] = useState(null);
  const [patientData, setPatientData] = useState(null);
  
  // AI Chat states
  const [aiQuery, setAiQuery] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  
  // Intervention states
  const [interventions, setInterventions] = useState([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    file_name: '',
    file_type: 'csv',
    description: '',
    intervention_type: 'data_import'
  });
  
  // Intervention AI Chat states
  const [interventionQuery, setInterventionQuery] = useState('');
  const [interventionLoading, setInterventionLoading] = useState(false);
  const [interventionChatHistory, setInterventionChatHistory] = useState([]);
  const [documentContent, setDocumentContent] = useState('');
  const [showDocumentDialog, setShowDocumentDialog] = useState(false);
  
  // Export/Email states
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [exporting, setExporting] = useState(false);
  
  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk';
  const isDirector = ['director', 'managing_director'].includes(user?.role);
  
  const fetchPermissions = useCallback(async () => {
    try {
      const res = await api.get('/analytics/permissions');
      setPermissions(res.data);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
    }
  }, [api]);
  
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, finRes, staffRes, patRes, permRes] = await Promise.all([
        api.get(`/analytics/dashboard-summary?time_range=${timeRange}`).catch(() => ({ data: null })),
        api.get(`/analytics/financial-overview?time_range=${timeRange}`).catch(() => ({ data: null })),
        api.get(`/analytics/staff-performance?time_range=${timeRange}`).catch(() => ({ data: null })),
        api.get(`/analytics/patient-analytics?time_range=${timeRange}`).catch(() => ({ data: null })),
        api.get('/analytics/permissions').catch(() => ({ data: null }))
      ]);
      
      setDashboardData(dashRes.data);
      setFinancialData(finRes.data);
      setStaffData(staffRes.data);
      setPatientData(patRes.data);
      if (permRes.data) setPermissions(permRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [api, timeRange]);
  
  const fetchInterventions = useCallback(async () => {
    try {
      const res = await api.get('/analytics/interventions');
      setInterventions(res.data.interventions || []);
    } catch (error) {
      console.error('Failed to fetch interventions:', error);
    }
  }, [api]);
  
  useEffect(() => {
    fetchAllData();
    fetchInterventions();
  }, [fetchAllData, fetchInterventions]);
  
  // AI Query Handler - All Directors can generate
  const handleAiQuery = async () => {
    if (!aiQuery.trim() || aiLoading) return;
    
    setAiLoading(true);
    setChatHistory(prev => [...prev, { role: 'user', content: aiQuery }]);
    
    try {
      const response = await api.post('/analytics/ai-analyze', null, {
        params: { query: aiQuery, include_charts: true }
      });
      
      setChatHistory(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response,
        data: response.data.data_summary,
        charts: response.data.suggested_charts
      }]);
      setAiQuery('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get AI response');
      setChatHistory(prev => [...prev, { 
        role: 'assistant', 
        content: 'I apologize, but I encountered an error processing your request.',
        error: true
      }]);
    } finally {
      setAiLoading(false);
    }
  };
  
  // Export Handler - Super Admin only
  const handleExport = async (format) => {
    if (!permissions.can_export) {
      toast.error('Only Super Admin can export reports');
      return;
    }
    
    setExporting(true);
    try {
      const res = await api.post('/analytics/export-report', null, {
        params: { report_type: 'comprehensive', format }
      });
      
      if (res.data.success) {
        // Generate downloadable content
        const reportData = res.data.report_data;
        const content = JSON.stringify(reportData, null, 2);
        const blob = new Blob([content], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ICRCL_Analytics_Report_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        toast.success('Report exported successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export report');
    } finally {
      setExporting(false);
    }
  };
  
  // Print Handler - Super Admin only
  const handlePrint = () => {
    if (!permissions.can_print) {
      toast.error('Only Super Admin can print reports');
      return;
    }
    window.print();
    toast.success('Print dialog opened');
  };
  
  // Email Handler - Super Admin only
  const handleSendEmail = async () => {
    if (!permissions.can_email) {
      toast.error('Only Super Admin can email reports');
      return;
    }
    
    if (!emailRecipient.trim()) {
      toast.error('Please enter a recipient email');
      return;
    }
    
    try {
      const res = await api.post('/analytics/send-report-email', null, {
        params: { recipient_email: emailRecipient, report_type: 'comprehensive' }
      });
      
      if (res.data.success) {
        toast.success(`Report will be sent to ${emailRecipient}`);
        setShowEmailDialog(false);
        setEmailRecipient('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    }
  };
  
  // Upload Intervention Handler - Super Admin only
  const handleUploadIntervention = async () => {
    if (!permissions.can_upload_interventions) {
      toast.error('Only Super Admin can upload intervention files');
      return;
    }
    
    if (!uploadForm.file_name.trim() || !uploadForm.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      const res = await api.post('/analytics/upload-intervention', null, {
        params: uploadForm
      });
      
      if (res.data.success) {
        toast.success('Intervention file uploaded successfully');
        setShowUploadDialog(false);
        setUploadForm({ file_name: '', file_type: 'csv', description: '', intervention_type: 'data_import' });
        fetchInterventions();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload intervention');
    }
  };
  
  // Delete Intervention Handler - Super Admin only
  const handleDeleteIntervention = async (id) => {
    if (!permissions.can_upload_interventions) {
      toast.error('Only Super Admin can delete intervention files');
      return;
    }
    
    try {
      await api.delete(`/analytics/interventions/${id}`);
      toast.success('Intervention deleted successfully');
      fetchInterventions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete intervention');
    }
  };
  
  // Intervention AI Chat Handler - with web search and document analysis
  const handleInterventionChat = async () => {
    if (!interventionQuery.trim() || interventionLoading) return;
    
    if (!permissions.can_upload_interventions) {
      toast.error('Only Super Admin can use intervention AI chat');
      return;
    }
    
    setInterventionLoading(true);
    setInterventionChatHistory(prev => [...prev, { role: 'user', content: interventionQuery }]);
    
    try {
      const response = await api.post('/analytics/intervention-chat', null, {
        params: { 
          query: interventionQuery, 
          include_web_search: true,
          document_context: documentContent || null
        }
      });
      
      setInterventionChatHistory(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response,
        context: response.data.context_used
      }]);
      setInterventionQuery('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get AI response');
      setInterventionChatHistory(prev => [...prev, { 
        role: 'assistant', 
        content: 'I apologize, but I encountered an error processing your request.',
        error: true
      }]);
    } finally {
      setInterventionLoading(false);
    }
  };
  
  if (!isDirector) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Brain className="w-16 h-16 text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700">AI Analytics Dashboard</h2>
        <p className="text-slate-500 mt-2">This dashboard is restricted to Directors only.</p>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#2a9d8f] animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading analytics data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in print:space-y-4" data-testid="analytics-dashboard" ref={printRef}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 print:hidden">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f] flex items-center gap-2">
            <Brain className="w-7 h-7" />
            AI Analytics Dashboard
            {isSuperAdmin && <Crown className="w-5 h-5 text-amber-500" />}
          </h1>
          <p className="text-slate-500 mt-1">
            Comprehensive centre analytics with AI-powered insights
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Time Range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">Last 7 Days</SelectItem>
              <SelectItem value="month">Last 30 Days</SelectItem>
              <SelectItem value="quarter">Last 90 Days</SelectItem>
              <SelectItem value="year">Last Year</SelectItem>
              <SelectItem value="all_time">All Time</SelectItem>
            </SelectContent>
          </Select>
          
          <Button onClick={fetchAllData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
          
          {/* Super Admin Only Actions */}
          {permissions.can_export && (
            <>
              <Button onClick={() => handleExport('json')} variant="outline" size="sm" disabled={exporting} data-testid="export-btn">
                <Download className="w-4 h-4 mr-1" />
                {exporting ? 'Exporting...' : 'Download'}
              </Button>
              <Button onClick={handlePrint} variant="outline" size="sm" data-testid="print-btn">
                <Printer className="w-4 h-4 mr-1" />
                Print
              </Button>
              <Button onClick={() => setShowEmailDialog(true)} variant="outline" size="sm" data-testid="email-btn">
                <Mail className="w-4 h-4 mr-1" />
                Email
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Print Header - Only visible when printing */}
      <div className="hidden print:block text-center mb-6">
        <h1 className="text-2xl font-bold text-[#1e3a5f]">ICRCL Analytics Report</h1>
        <p className="text-sm text-slate-500">Generated on {new Date().toLocaleDateString('en-GB')}</p>
      </div>

      {/* Quick Stats */}
      {dashboardData?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 print:grid-cols-4">
          <Card className="bg-gradient-to-br from-[#2a9d8f] to-[#1e3a5f] text-white print:bg-white print:text-black print:border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm print:text-slate-600">Total Revenue</p>
                  <p className="text-2xl font-bold">£{dashboardData.summary.total_revenue?.toLocaleString()}</p>
                </div>
                <PoundSterling className="w-8 h-8 text-white/60 print:text-slate-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white print:bg-white print:text-black print:border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm print:text-slate-600">Active Children</p>
                  <p className="text-2xl font-bold">{dashboardData.summary.total_patients}</p>
                </div>
                <Baby className="w-8 h-8 text-white/60 print:text-slate-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white print:bg-white print:text-black print:border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm print:text-slate-600">Staff Members</p>
                  <p className="text-2xl font-bold">{dashboardData.summary.total_staff}</p>
                </div>
                <Users className="w-8 h-8 text-white/60 print:text-slate-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white print:bg-white print:text-black print:border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm print:text-slate-600">Total Sessions</p>
                  <p className="text-2xl font-bold">{dashboardData.summary.total_sessions}</p>
                </div>
                <Calendar className="w-8 h-8 text-white/60 print:text-slate-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="print:hidden">
        <TabsList className="flex flex-wrap gap-1">
          <TabsTrigger value="overview" data-testid="overview-tab">
            <BarChart3 className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="financial" data-testid="financial-tab">
            <PoundSterling className="w-4 h-4 mr-2" />
            Financial
          </TabsTrigger>
          <TabsTrigger value="staff" data-testid="staff-tab">
            <Trophy className="w-4 h-4 mr-2" />
            Staff Performance
          </TabsTrigger>
          <TabsTrigger value="patients" data-testid="patients-tab">
            <Baby className="w-4 h-4 mr-2" />
            Children
          </TabsTrigger>
          <TabsTrigger value="ai-assistant" data-testid="ai-tab">
            <Sparkles className="w-4 h-4 mr-2" />
            AI Assistant
          </TabsTrigger>
          {permissions.can_upload_interventions && (
            <TabsTrigger value="interventions" data-testid="interventions-tab">
              <FileUp className="w-4 h-4 mr-2" />
              Interventions
            </TabsTrigger>
          )}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Trend */}
            {financialData?.revenue_by_month && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#2a9d8f]" />
                    Revenue Trend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={financialData.revenue_by_month}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(v) => `£${v}`} />
                      <Tooltip formatter={(v) => `£${v.toLocaleString()}`} />
                      <Area type="monotone" dataKey="revenue" stroke="#2a9d8f" fill="#2a9d8f" fillOpacity={0.3} />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Invoice Status Distribution */}
            {financialData?.invoice_status && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieIcon className="w-5 h-5 text-[#1e3a5f]" />
                    Invoice Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={financialData.invoice_status}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        dataKey="count"
                        nameKey="status"
                        label={({ status, count }) => `${status}: ${count}`}
                      >
                        {financialData.invoice_status.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Best Performers */}
            {staffData?.best_performers && staffData.best_performers.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    Top Performers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {staffData.best_performers.slice(0, 5).map((performer, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                            idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-amber-700' : 'bg-slate-300'
                          }`}>
                            {idx + 1}
                          </div>
                          <div>
                            <p className="font-medium">{performer.name}</p>
                            <p className="text-xs text-slate-500">{performer.sessions} sessions • {performer.assessments} assessments</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-[#2a9d8f]">£{performer.revenue_generated?.toLocaleString()}</p>
                          <p className="text-xs text-slate-500">revenue generated</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Top Children by Revenue */}
            {patientData?.children_by_revenue && patientData.children_by_revenue.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Baby className="w-5 h-5 text-blue-500" />
                    Top Children by Revenue
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={patientData.children_by_revenue.slice(0, 8)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tickFormatter={(v) => `£${v}`} />
                      <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(v) => `£${v.toLocaleString()}`} />
                      <Bar dataKey="revenue" fill="#1e3a5f" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Financial Tab */}
        <TabsContent value="financial">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue by Service */}
            {financialData?.revenue_by_service && (
              <Card>
                <CardHeader>
                  <CardTitle>Revenue by Service Type</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie
                        data={financialData.revenue_by_service}
                        cx="50%"
                        cy="50%"
                        outerRadius={120}
                        dataKey="revenue"
                        nameKey="service"
                        label={({ service }) => `${service?.substring(0, 15) || 'Other'}...`}
                      >
                        {financialData.revenue_by_service.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => `£${v.toLocaleString()}`} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Revenue Split - Therapist vs Centre */}
            {financialData?.revenue_split && (
              <Card>
                <CardHeader>
                  <CardTitle>Revenue Split</CardTitle>
                  <CardDescription>Therapist earnings vs Centre revenue</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Therapist Earnings', value: financialData.revenue_split.therapist_total },
                            { name: 'Centre Revenue', value: financialData.revenue_split.centre_total }
                          ]}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          dataKey="value"
                        >
                          <Cell fill="#2a9d8f" />
                          <Cell fill="#1e3a5f" />
                        </Pie>
                        <Tooltip formatter={(v) => `£${v.toLocaleString()}`} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                    
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="p-3 bg-[#2a9d8f]/10 rounded-lg">
                        <p className="text-2xl font-bold text-[#2a9d8f]">£{financialData.revenue_split.therapist_total?.toLocaleString()}</p>
                        <p className="text-xs text-slate-600">Therapist Earnings</p>
                      </div>
                      <div className="p-3 bg-[#1e3a5f]/10 rounded-lg">
                        <p className="text-2xl font-bold text-[#1e3a5f]">£{financialData.revenue_split.centre_total?.toLocaleString()}</p>
                        <p className="text-xs text-slate-600">Centre Revenue</p>
                      </div>
                      <div className="p-3 bg-amber-50 rounded-lg">
                        <p className="text-2xl font-bold text-amber-600">£{financialData.revenue_split.total_billed?.toLocaleString()}</p>
                        <p className="text-xs text-slate-600">Total Billed</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Therapist Earnings Table */}
            {financialData?.therapist_earnings && financialData.therapist_earnings.length > 0 && (
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    Top Earning Therapists
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={financialData.therapist_earnings}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="_id" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                      <YAxis tickFormatter={(v) => `£${v}`} />
                      <Tooltip formatter={(v) => `£${v?.toLocaleString()}`} />
                      <Legend />
                      <Bar dataKey="total_earnings" name="Earnings" fill="#2a9d8f" />
                      <Bar dataKey="total_billed" name="Billed" fill="#1e3a5f" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Staff Performance Tab */}
        <TabsContent value="staff">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sessions by Therapist */}
            {staffData?.therapist_by_sessions && (
              <Card>
                <CardHeader>
                  <CardTitle>Sessions by Therapist</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={staffData.therapist_by_sessions.slice(0, 10)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="sessions" fill="#2a9d8f" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Role Distribution */}
            {staffData?.role_distribution && (
              <Card>
                <CardHeader>
                  <CardTitle>Staff Role Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie
                        data={staffData.role_distribution}
                        cx="50%"
                        cy="50%"
                        outerRadius={120}
                        dataKey="count"
                        nameKey="role"
                        label={({ role, count }) => `${role}: ${count}`}
                      >
                        {staffData.role_distribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Best Performers Detailed */}
            {staffData?.best_performers && (
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    Performance Leaderboard
                  </CardTitle>
                  <CardDescription>Based on sessions, assessments, evaluations, and revenue</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-2">Rank</th>
                          <th className="text-left py-3 px-2">Therapist</th>
                          <th className="text-center py-3 px-2">Sessions</th>
                          <th className="text-center py-3 px-2">Assessments</th>
                          <th className="text-center py-3 px-2">Evaluations</th>
                          <th className="text-right py-3 px-2">Revenue</th>
                          <th className="text-right py-3 px-2">Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {staffData.best_performers.map((p, idx) => (
                          <tr key={idx} className={`border-b ${idx === 0 ? 'bg-amber-50' : idx === 1 ? 'bg-slate-50' : idx === 2 ? 'bg-orange-50' : ''}`}>
                            <td className="py-3 px-2">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                                idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-amber-700' : 'bg-slate-300'
                              }`}>
                                {idx + 1}
                              </div>
                            </td>
                            <td className="py-3 px-2 font-medium">{p.name}</td>
                            <td className="text-center py-3 px-2">{p.sessions}</td>
                            <td className="text-center py-3 px-2">{p.assessments}</td>
                            <td className="text-center py-3 px-2">{p.evaluations}</td>
                            <td className="text-right py-3 px-2 text-[#2a9d8f] font-semibold">£{p.revenue_generated?.toLocaleString()}</td>
                            <td className="text-right py-3 px-2 font-bold">{Math.round(p.performance_score)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Patients/Children Tab */}
        <TabsContent value="patients">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Children by Sessions */}
            {patientData?.children_by_sessions && (
              <Card>
                <CardHeader>
                  <CardTitle>Children by Session Count</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={patientData.children_by_sessions.slice(0, 10)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="sessions" fill="#1e3a5f" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Age Distribution */}
            {patientData?.age_distribution && (
              <Card>
                <CardHeader>
                  <CardTitle>Age Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie
                        data={patientData.age_distribution}
                        cx="50%"
                        cy="50%"
                        outerRadius={120}
                        dataKey="count"
                        nameKey="age_group"
                        label={({ age_group, count }) => count > 0 ? `${age_group}: ${count}` : ''}
                      >
                        {patientData.age_distribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Children Revenue Contribution */}
            {patientData?.children_by_revenue && (
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PoundSterling className="w-5 h-5 text-[#2a9d8f]" />
                    Children - Revenue Contribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={patientData.children_by_revenue.slice(0, 15)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                      <YAxis tickFormatter={(v) => `£${v}`} />
                      <Tooltip formatter={(v) => `£${v?.toLocaleString()}`} />
                      <Legend />
                      <Bar dataKey="revenue" name="Revenue" fill="#2a9d8f" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Discipline Distribution */}
            {patientData?.discipline_distribution && patientData.discipline_distribution.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Patients by Discipline</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={patientData.discipline_distribution}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="discipline" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="patients" fill="#e76f51" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* AI Assistant Tab */}
        <TabsContent value="ai-assistant">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* AI Chat */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#2a9d8f]" />
                  AI Analytics Assistant
                  <Badge className="bg-green-500 ml-2">All Directors</Badge>
                </CardTitle>
                <CardDescription>
                  Ask any question about your centre - financial status, staff performance, patient data, trends, and insights
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] border rounded-lg p-4 mb-4">
                  {chatHistory.length === 0 ? (
                    <div className="text-center py-12 text-slate-400">
                      <Bot className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Start a conversation to get AI-powered analytics insights</p>
                      <div className="mt-4 space-y-2">
                        <p className="text-sm font-medium text-slate-500">Try asking:</p>
                        <div className="flex flex-wrap gap-2 justify-center">
                          {[
                            "What is the complete financial status?",
                            "Who is the best performing therapist?",
                            "Which child brings the most revenue?",
                            "Show me a summary of my centre",
                            "Compare revenue this month vs last month",
                            "What are the key performance indicators?"
                          ].map((q, i) => (
                            <Button 
                              key={i} 
                              variant="outline" 
                              size="sm"
                              onClick={() => setAiQuery(q)}
                              className="text-xs"
                            >
                              {q}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {chatHistory.map((msg, idx) => (
                        <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                          {msg.role === 'assistant' && (
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${msg.error ? 'bg-red-100' : 'bg-[#2a9d8f]/10'}`}>
                              <Bot className={`w-4 h-4 ${msg.error ? 'text-red-500' : 'text-[#2a9d8f]'}`} />
                            </div>
                          )}
                          <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                            msg.role === 'user'
                              ? 'bg-[#1e3a5f] text-white'
                              : msg.error
                              ? 'bg-red-50 text-red-700'
                              : 'bg-slate-100 text-slate-800'
                          }`}>
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          </div>
                          {msg.role === 'user' && (
                            <div className="w-8 h-8 rounded-full bg-[#1e3a5f] flex items-center justify-center flex-shrink-0">
                              <Users className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                      ))}
                      {aiLoading && (
                        <div className="flex gap-3">
                          <div className="w-8 h-8 rounded-full bg-[#2a9d8f]/10 flex items-center justify-center">
                            <Bot className="w-4 h-4 text-[#2a9d8f]" />
                          </div>
                          <div className="bg-slate-100 rounded-2xl px-4 py-3">
                            <div className="flex gap-1">
                              <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                              <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                              <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </ScrollArea>
                
                <div className="flex gap-2">
                  <Input
                    value={aiQuery}
                    onChange={(e) => setAiQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAiQuery()}
                    placeholder="Ask about financial status, best performers, trends..."
                    disabled={aiLoading}
                    data-testid="ai-analytics-input"
                  />
                  <Button 
                    onClick={handleAiQuery} 
                    disabled={aiLoading || !aiQuery.trim()}
                    className="bg-[#2a9d8f]"
                    data-testid="ai-analytics-send"
                  >
                    {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Quick Insights Panel */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-[#1e3a5f]" />
                  Quick Insights
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Best Performer */}
                {staffData?.best_performers?.[0] && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Trophy className="w-4 h-4 text-amber-500" />
                      <span className="text-xs font-medium text-amber-700">Best Performer</span>
                    </div>
                    <p className="font-semibold">{staffData.best_performers[0].name}</p>
                    <p className="text-xs text-slate-500">£{staffData.best_performers[0].revenue_generated?.toLocaleString()} revenue</p>
                  </div>
                )}

                {/* Top Revenue Child */}
                {patientData?.children_by_revenue?.[0] && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Baby className="w-4 h-4 text-blue-500" />
                      <span className="text-xs font-medium text-blue-700">Top Revenue Child</span>
                    </div>
                    <p className="font-semibold">{patientData.children_by_revenue[0].name}</p>
                    <p className="text-xs text-slate-500">£{patientData.children_by_revenue[0].revenue?.toLocaleString()} • {patientData.children_by_revenue[0].sessions} sessions</p>
                  </div>
                )}

                {/* Total Revenue */}
                {dashboardData?.summary && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <PoundSterling className="w-4 h-4 text-green-600" />
                      <span className="text-xs font-medium text-green-700">Total Revenue</span>
                    </div>
                    <p className="font-semibold text-lg">£{dashboardData.summary.total_revenue?.toLocaleString()}</p>
                    <p className="text-xs text-slate-500">£{dashboardData.summary.pending_revenue?.toLocaleString()} pending</p>
                  </div>
                )}

                {/* Quick Stats */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2 bg-slate-50 rounded text-center">
                    <p className="text-lg font-bold text-[#1e3a5f]">{dashboardData?.summary?.total_patients || 0}</p>
                    <p className="text-xs text-slate-500">Children</p>
                  </div>
                  <div className="p-2 bg-slate-50 rounded text-center">
                    <p className="text-lg font-bold text-[#1e3a5f]">{dashboardData?.summary?.total_staff || 0}</p>
                    <p className="text-xs text-slate-500">Staff</p>
                  </div>
                  <div className="p-2 bg-slate-50 rounded text-center">
                    <p className="text-lg font-bold text-[#1e3a5f]">{dashboardData?.summary?.total_sessions || 0}</p>
                    <p className="text-xs text-slate-500">Sessions</p>
                  </div>
                  <div className="p-2 bg-slate-50 rounded text-center">
                    <p className="text-lg font-bold text-[#1e3a5f]">{dashboardData?.summary?.total_invoices || 0}</p>
                    <p className="text-xs text-slate-500">Invoices</p>
                  </div>
                </div>

                {/* Super Admin Actions */}
                {permissions.can_export && (
                  <div className="border-t pt-4 mt-4">
                    <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1">
                      <Crown className="w-3 h-3 text-amber-500" />
                      Super Admin Actions
                    </p>
                    <div className="flex flex-col gap-2">
                      <Button onClick={() => handleExport('json')} variant="outline" size="sm" className="w-full justify-start">
                        <Download className="w-4 h-4 mr-2" />
                        Download Report
                      </Button>
                      <Button onClick={handlePrint} variant="outline" size="sm" className="w-full justify-start">
                        <Printer className="w-4 h-4 mr-2" />
                        Print Report
                      </Button>
                      <Button onClick={() => setShowEmailDialog(true)} variant="outline" size="sm" className="w-full justify-start">
                        <Mail className="w-4 h-4 mr-2" />
                        Email Report
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Interventions Tab - Super Admin Only */}
        {permissions.can_upload_interventions && (
          <TabsContent value="interventions">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* AI Intervention Chat */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Brain className="w-5 h-5 text-[#2a9d8f]" />
                    AI Business Intelligence
                    <Badge className="bg-amber-500">Super Admin Only</Badge>
                  </CardTitle>
                  <CardDescription>
                    AI-powered business analysis with real-world market comparisons and document analysis
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Feature Badges */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Badge variant="outline" className="text-xs">
                      <Globe className="w-3 h-3 mr-1" />
                      Web Search Enabled
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      <Search className="w-3 h-3 mr-1" />
                      Market Comparisons
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      <FileSearch className="w-3 h-3 mr-1" />
                      Document Analysis
                    </Badge>
                  </div>

                  <ScrollArea className="h-[350px] border rounded-lg p-4 mb-4">
                    {interventionChatHistory.length === 0 ? (
                      <div className="text-center py-12 text-slate-400">
                        <Brain className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="font-medium">AI Business Intelligence Assistant</p>
                        <p className="text-sm mt-2">Ask about market comparisons, competitor policies, pricing strategies, and policy templates</p>
                        <div className="mt-4 space-y-2">
                          <p className="text-xs font-medium text-slate-500">Try asking:</p>
                          <div className="flex flex-wrap gap-2 justify-center">
                            {[
                              "Compare our pricing with similar therapy centres in London",
                              "What policies do similar centres like The Children's Trust have?",
                              "Generate a Safeguarding Policy template for ICRCL",
                              "What CQC regulations should we be aware of?",
                              "List all essential policies needed for our centre",
                              "Analyze competitor policies and best practices"
                            ].map((q, i) => (
                              <Button 
                                key={i} 
                                variant="outline" 
                                size="sm"
                                onClick={() => setInterventionQuery(q)}
                                className="text-xs"
                              >
                                {q}
                              </Button>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {interventionChatHistory.map((msg, idx) => (
                          <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                            {msg.role === 'assistant' && (
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${msg.error ? 'bg-red-100' : 'bg-[#2a9d8f]/10'}`}>
                                <Brain className={`w-4 h-4 ${msg.error ? 'text-red-500' : 'text-[#2a9d8f]'}`} />
                              </div>
                            )}
                            <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                              msg.role === 'user'
                                ? 'bg-[#1e3a5f] text-white'
                                : msg.error
                                ? 'bg-red-50 text-red-700'
                                : 'bg-slate-100 text-slate-800'
                            }`}>
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                              {msg.context && (
                                <div className="flex gap-2 mt-2 pt-2 border-t border-slate-200">
                                  {msg.context.web_search && (
                                    <Badge variant="outline" className="text-xs bg-white">
                                      <Globe className="w-3 h-3 mr-1" />
                                      Web Data
                                    </Badge>
                                  )}
                                  {msg.context.document_analysis && (
                                    <Badge variant="outline" className="text-xs bg-white">
                                      <FileSearch className="w-3 h-3 mr-1" />
                                      Doc Analysis
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </div>
                            {msg.role === 'user' && (
                              <div className="w-8 h-8 rounded-full bg-[#1e3a5f] flex items-center justify-center flex-shrink-0">
                                <Crown className="w-4 h-4 text-amber-400" />
                              </div>
                            )}
                          </div>
                        ))}
                        {interventionLoading && (
                          <div className="flex gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#2a9d8f]/10 flex items-center justify-center">
                              <Brain className="w-4 h-4 text-[#2a9d8f]" />
                            </div>
                            <div className="bg-slate-100 rounded-2xl px-4 py-3">
                              <div className="flex gap-1">
                                <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                                <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                                <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </ScrollArea>
                  
                  {/* Document Context Input */}
                  {documentContent && (
                    <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileSearch className="w-4 h-4 text-blue-500" />
                          <span className="text-sm text-blue-700 font-medium">Document loaded for analysis</span>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setDocumentContent('')}
                          className="text-blue-500 hover:text-blue-700"
                        >
                          Clear
                        </Button>
                      </div>
                      <p className="text-xs text-blue-600 mt-1 truncate">{documentContent.substring(0, 100)}...</p>
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowDocumentDialog(true)}
                      className="flex-shrink-0"
                    >
                      <FileUp className="w-4 h-4" />
                    </Button>
                    <Input
                      value={interventionQuery}
                      onChange={(e) => setInterventionQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleInterventionChat()}
                      placeholder="Ask about market comparisons, competitors, strategies..."
                      disabled={interventionLoading}
                      data-testid="intervention-chat-input"
                    />
                    <Button 
                      onClick={handleInterventionChat} 
                      disabled={interventionLoading || !interventionQuery.trim()}
                      className="bg-[#2a9d8f]"
                      data-testid="intervention-chat-send"
                    >
                      {interventionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Uploaded Files & Quick Actions */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileUp className="w-4 h-4 text-[#2a9d8f]" />
                      Intervention Files
                    </CardTitle>
                    <Button size="sm" onClick={() => setShowUploadDialog(true)} className="bg-[#2a9d8f]">
                      <Upload className="w-3 h-3 mr-1" />
                      Upload
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px]">
                    {interventions.length === 0 ? (
                      <div className="text-center py-8 text-slate-400">
                        <FileUp className="w-10 h-10 mx-auto mb-3 opacity-50" />
                        <p className="text-sm">No files uploaded</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {interventions.map((intervention) => (
                          <div key={intervention.id} className="p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-[#2a9d8f]" />
                                <div>
                                  <p className="text-sm font-medium">{intervention.file_name}</p>
                                  <p className="text-xs text-slate-500">{intervention.intervention_type}</p>
                                </div>
                              </div>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDeleteIntervention(intervention.id)}
                                className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                            <p className="text-xs text-slate-400 mt-1">{intervention.description}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                  
                  {/* Quick Analysis Prompts */}
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-xs font-medium text-slate-500 mb-2">Quick Analysis</p>
                    <div className="flex flex-col gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="justify-start text-xs"
                        onClick={() => setInterventionQuery("Compare ICRCL's pricing with therapy centres in East London")}
                      >
                        <Search className="w-3 h-3 mr-2" />
                        Compare Local Prices
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="justify-start text-xs"
                        onClick={() => setInterventionQuery("What are the current CQC compliance requirements for children's therapy centres?")}
                      >
                        <Globe className="w-3 h-3 mr-2" />
                        CQC Requirements
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="justify-start text-xs"
                        onClick={() => setInterventionQuery("Analyze our financial performance and suggest improvements")}
                      >
                        <TrendingUp className="w-3 h-3 mr-2" />
                        Financial Review
                      </Button>
                    </div>
                  </div>
                  
                  {/* Policy Templates */}
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-xs font-medium text-slate-500 mb-2">Policy Templates</p>
                    <div className="flex flex-col gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="justify-start text-xs"
                        onClick={() => setInterventionQuery("Generate a complete Safeguarding and Child Protection Policy template for ICRCL, referencing similar therapy centres like The Children's Trust and Bobath Centre. Make it CQC compliant.")}
                      >
                        <FileText className="w-3 h-3 mr-2" />
                        Safeguarding Policy
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="justify-start text-xs"
                        onClick={() => setInterventionQuery("Generate a Data Protection and GDPR Policy template for ICRCL children's therapy centre. Include UK GDPR requirements and reference industry best practices.")}
                      >
                        <FileText className="w-3 h-3 mr-2" />
                        GDPR Policy
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="justify-start text-xs"
                        onClick={() => setInterventionQuery("Generate a Complaints and Feedback Policy template for ICRCL. Make it CQC compliant and reference how similar centres like The Children's Trust handle complaints.")}
                      >
                        <FileText className="w-3 h-3 mr-2" />
                        Complaints Policy
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="justify-start text-xs"
                        onClick={() => setInterventionQuery("List all essential policies needed for a CQC-registered children's therapy centre like ICRCL, and provide a brief description of each.")}
                      >
                        <FileText className="w-3 h-3 mr-2" />
                        All Required Policies
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="justify-start text-xs"
                        onClick={() => setInterventionQuery("Analyze policies from similar therapy centres (The Children's Trust, Bobath Centre, TreeHouse School) and summarize their key policy frameworks and best practices.")}
                      >
                        <Search className="w-3 h-3 mr-2" />
                        Competitor Policies
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Document Upload Dialog for Analysis */}
      <Dialog open={showDocumentDialog} onOpenChange={setShowDocumentDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSearch className="w-5 h-5" />
              Add Document for AI Analysis
            </DialogTitle>
            <DialogDescription>
              Paste document content or text for the AI to analyze alongside your questions
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Document Content</Label>
              <Textarea
                value={documentContent}
                onChange={(e) => setDocumentContent(e.target.value)}
                placeholder="Paste your document content, financial data, competitor information, or any text you want the AI to analyze..."
                rows={12}
                className="font-mono text-sm"
              />
              <p className="text-xs text-slate-500 mt-2">
                Tip: You can paste spreadsheet data, reports, competitor pricing, or any text for analysis
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocumentDialog(false)}>Cancel</Button>
            <Button 
              onClick={() => {
                if (documentContent.trim()) {
                  toast.success('Document loaded for analysis');
                  setShowDocumentDialog(false);
                } else {
                  toast.error('Please enter document content');
                }
              }} 
              className="bg-[#2a9d8f]"
            >
              <FileSearch className="w-4 h-4 mr-2" />
              Load for Analysis
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Email Analytics Report
            </DialogTitle>
            <DialogDescription>
              Send the comprehensive analytics report to an email address
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Recipient Email</Label>
              <Input
                type="email"
                value={emailRecipient}
                onChange={(e) => setEmailRecipient(e.target.value)}
                placeholder="Enter email address"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Cancel</Button>
            <Button onClick={handleSendEmail} className="bg-[#2a9d8f]">
              <Send className="w-4 h-4 mr-2" />
              Send Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Intervention Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload Intervention File
            </DialogTitle>
            <DialogDescription>
              Upload a file for data intervention and analysis
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>File Name *</Label>
              <Input
                value={uploadForm.file_name}
                onChange={(e) => setUploadForm(prev => ({ ...prev, file_name: e.target.value }))}
                placeholder="e.g., Q4_Financial_Data.csv"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>File Type</Label>
                <Select
                  value={uploadForm.file_type}
                  onValueChange={(v) => setUploadForm(prev => ({ ...prev, file_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="csv">CSV</SelectItem>
                    <SelectItem value="xlsx">Excel (XLSX)</SelectItem>
                    <SelectItem value="json">JSON</SelectItem>
                    <SelectItem value="pdf">PDF</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Intervention Type</Label>
                <Select
                  value={uploadForm.intervention_type}
                  onValueChange={(v) => setUploadForm(prev => ({ ...prev, intervention_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="data_import">Data Import</SelectItem>
                    <SelectItem value="correction">Data Correction</SelectItem>
                    <SelectItem value="analysis">Analysis Request</SelectItem>
                    <SelectItem value="backup">Backup</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the purpose of this intervention file..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
            <Button onClick={handleUploadIntervention} className="bg-[#2a9d8f]">
              <Upload className="w-4 h-4 mr-2" />
              Upload File
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
